import contractsRepo from '@/repositories/contractsRepository';
import customerRepo from '@/repositories/customerRepository';
import enterpriseRepo from '@/repositories/enterpriseRepository';
import simulationRepo from '@/repositories/simulationRepository';
import i18n from '@/plugins/i18n';
import { getField, updateField } from 'vuex-map-fields';
import Vue from 'vue';
import moment from 'moment';

const getDefaultState = () => ({
  customer: null,
  insuranceBroker: null,
  enterprise: null,
  primaryContract: null,
  secondaryContracts: [],
  simulations: [],
  isLoading: false,
  error: false,
  limit: 3,
  offset: 0,
  isLastPage: false,
});

export const monthsToYears = (months) => {
  let yearsT = '';
  let monthsT = '';
  let andT = '';
  if (months !== 0) {
    const years = Math.floor(months / 12);
    if (years === 1) {
      yearsT = `1 ${i18n.t('generic.single_year')} `;
    } else if (years > 1) {
      yearsT = `${years} ${i18n.t('generic.years')} `;
    }

    const remainder = months % 12;
    if (remainder !== 0) {
      monthsT = `${i18n.tc('generic.month', remainder)}`;
    }

    if (yearsT && monthsT) {
      andT = `${i18n.t('generic.and')} `;
    }

    return yearsT + andT + monthsT;
  } else {
    return `${i18n.tc('generic.month', 0)}`;
  }
};

export const totalYearsWorked = (startDate) => {
  const workedDays = moment('31/12/2020', 'DD/MM/YYYY').diff(moment(startDate, 'YYYY-MM-DD'), 'day');
  return Math.ceil(workedDays / 365.25 * 12);
};

const contractTypes = () => (
  [
    { id: 1, key: 'VAPZ' },
    { id: 3, key: 'RIZIV' },
    { id: 6, key: 'GroupInsuranceInEnterprise' },
    { id: 8, key: 'IPTInEnterprise' },
    { id: 10, key: 'InternalPensionPromise' },
    { id: 15, key: 'BLVInEnterprise' },
    { id: 20, key: 'POZ' },
    { id: 21, key: 'IPTOutCompany' },
    { id: 22, key: 'GroupInsuranceOutEnterprise' },
    { id: 23, key: 'BLVOutEnterprise' },
  ]
);

const maritalStates = () => (
  [
    { id: 1, key: 'married' },
    { id: 2, key: 'legallyCohabiting' },
    { id: 3, key: 'cohabiting' },
    { id: 4, key: 'single' },
    { id: 5, key: 'legallyDivorced' },
    { id: 6, key: 'widow' },
    { id: 8, key: 'unknown' },
  ]
);

const state = getDefaultState();

const getters = {
  getField,
  getCustomer(st) {
    return st.customer;
  },
  getInsuranceBroker(st) {
    return st.insuranceBroker;
  },
  getLimit(st) {
    return st.limit;
  },
  getOffset(st) {
    return st.offset;
  },
  getIsLastPage(st) {
    return st.isLastPage !== false;
  },
  getEnterprise(st) {
    return st.enterprise;
  },
  getPrimaryContract(st) {
    return st.primaryContract;
  },
  getSecondaryContracts(st) {
    return st.secondaryContracts;
  },
  getSimulations(st) {
    return st.simulations;
  },
  getIsLoading(st) {
    return st.isLoading;
  },
  getMaritalStates() {
    return maritalStates();
  },
  getCalculationResults() {
    return Object.keys(i18n.messages.en.lookUpValues.calculationResults);
  },
  getContractTypes() {
    return contractTypes();
  },
  getError(st) {
    return st.error;
  },
};

const mutations = {
  updateField,
  setCustomer(st, value) {
    st.customer = value;
  },
  setInsuranceBroker(st, value) {
    st.insuranceBroker = value;
  },
  setEnterprise(st, value) {
    st.enterprise = value;
  },
  setLimit(st, value) {
    st.limit = value;
  },
  setOffset(st, value) {
    st.offset = value;
  },
  setIsLastPage(st, value) {
    st.isLastPage = value;
  },
  setPrimaryContract(st, value) {
    st.primaryContract = value;
  },
  setSecondaryContracts(st, value) {
    st.secondaryContracts = value;
  },
  setSimulations(st, value) {
    st.simulations.push(...value);
  },
  setError(st, value) {
    st.error = value;
  },
  setIsLoading(st, value) {
    st.isLoading = value;
  },
  setEnterpriseDate(st, value) {
    st.enterprise.startDate = value;
  },
  updateSecondaryContract(st, value) {
    const index = st.secondaryContracts
      .findIndex(contract => contract.contractId === value.contractId);
    Vue.set(st.secondaryContracts, index, value);
  },
  addSecondaryContract(st, value) {
    st.secondaryContracts.push(value);
  },
  removeSecondaryContract(st, value) {
    const index = st.secondaryContracts
      .findIndex(contract => contract.contractId === value);

    st.secondaryContracts.splice(index, 1);
  },
  setCustomerProperties(st, value) {
    Object.keys(value).forEach((key) => { st.customer[key] = value[key]; });
  },
  setDefaultState(st) {
    Object.assign(st, getDefaultState());
  },
  setSimulationLoading(st, value) {
    const index = st.simulations.findIndex(s => s.calculationRequestId === value.id);
    st.simulations[index].loading = value.loading;

    Vue.set(st.simulations, index, st.simulations[index]);
  },
};
const actions = {
  // eslint-disable-next-line no-shadow
  async getContractDetails({ commit, dispatch, state }, contractId) {
    if (!(state.primaryContract && contractId === state.primaryContract.contractId)) {
      commit('setDefaultState');
      commit('setIsLoading', true);
      if (contractId !== '') {
        const response = await contractsRepo.getContractDetails(parseInt(contractId, 10));
        if (response && response.status === 200 && !response.data.errors && response.data.data.primaryContract) {
          const contract = response.data.data.primaryContract;
          commit('setPrimaryContract', {
            contractId: contract.contractId,
            contractType: contract.contractType,
            contractNumber: contract.contractNumber,
            includeYearsOutsideEnterprise: contract.includeYearsOutsideEnterprise,
            endDate: contract.endDate,
            situationDate: contract.situationDate,
            periodicity: contract.periodicity,
            annualPremiumAllIn: contract.annualPremiumAllIn,
            presentValue: contract.presentValue,
            insuranceCompany: contract.insuranceCompany,
            endAge: contract.endAge,
            commissionBackService: contract.commissionBackService,
            commissionPremiumPensionAndDeath: contract.commissionPremiumPensionAndDeath,
            canBeCalculated: contract.canBeCalculated,
            contractStatus: contract.contractStatus.contractStatusId,
          });
          commit('setCustomer', {
            customerId: contract.customer.customerId,
            fullName: `${contract.customer.firstName} ${contract.customer.lastName}`,
            maritalState: maritalStates().find(ms => ms.key === contract.customer.maritalState.name.charAt(0).toLowerCase() + contract.customer.maritalState.name.slice(1)),
            language: contract.customer.language ? contract.customer.language.code : '',
            fullAddress: `${contract.customer.street} ${contract.customer.number} ${contract.customer.bus}\n${contract.customer.postalCode} ${contract.customer.city}`,
            startDateCareer: contract.customer.startDateCareer,
            birthDate: contract.customer.birthDate,
            legalPensionAge: `${contract.customer.legalPensionAge} ${i18n.t('generic.years')}`,
            monthsIndependentBefore2021: contract.customer.monthsIndependentBefore2021,
            totalGrossRemuneration2020: contract.customer.totalGrossRemuneration2020,
          });
          commit('simulation/setDefaultValues', {
            maritalState: maritalStates().find(ms => ms.key === contract.customer.maritalState.name.charAt(0).toLowerCase() + contract.customer.maritalState.name.slice(1)),
            commissionBackService: state.primaryContract.commissionBackService,
            commissionPremium: state.primaryContract.commissionPremiumPensionAndDeath,
          }, { root: true });
          commit('setEnterprise', {
            enterpriseId: contract.enterprise.enterpriseId,
            enterpriseNumber: contract.enterprise.enterpriseNumber,
            name: contract.enterprise.name,
            startDate: contract.enterprise.startDateCareerInEnterprise,
          });
          commit('setInsuranceBroker', contract.insuranceBroker);
          commit('setSecondaryContracts', contract.customer.otherContracts);

          let language = '';
          switch (i18n.locale) {
            case ('nl'): language = 'nl-BE'; break;
            case ('en'): language = 'en-US'; break;
            case ('fr'): language = 'fr-FR'; break;
            default: language = 'nl-BE'; break;
          }

          dispatch('getSimulations',
            {
              contractNumber: state.primaryContract.contractNumber,
              language,
              page: { limit: state.limit, offset: state.offset },
            });
        } else {
          dispatch('general/setError', true, { root: true });
        }
      }
      commit('setIsLoading', false);
    }
  },
  async updateContract({ commit, dispatch }, contract) {
    commit('setIsLoading', true);
    if (contract) {
      const response = await contractsRepo.updateContract(contract);
      if (response && response.status === 200 && !response.data.errors) {
        const updatedContract = response.data.data.updateContract;
        commit('updateSecondaryContract', updatedContract);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setIsLoading', false);
  },
  // eslint-disable-next-line no-shadow
  async createContract({ commit, dispatch, state }, contract) {
    commit('setIsLoading', true);
    if (contract) {
      const response = await contractsRepo.createContract(state.primaryContract.contractId, contract);
      if (response && response.status === 200 && !response.data.errors) {
        const createdContract = response.data.data.createContract;
        commit('addSecondaryContract', createdContract);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setIsLoading', false);
  },
  async deleteContract({ commit, dispatch }, contractId) {
    commit('setIsLoading', true);
    if (contractId) {
      const response = await contractsRepo.deleteContract(contractId);
      if (response && response.status === 200 && !response.errors) {
        // const deletedContractId = response.data.data.deleteContract;
        commit('removeSecondaryContract', contractId);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setIsLoading', false);
  },
  // eslint-disable-next-line no-shadow
  async updateCustomer({ commit, dispatch, state }, customer) {
    commit('setIsLoading', true);
    if (customer) {
      const response = await customerRepo.updateCustomer(customer);
      if (response && response.status === 200 && !response.errors) {
        const updatedCustomer = response.data.data.updateCustomer;
        commit('setCustomerProperties', {
          customerId: updatedCustomer.customerId,
          maritalState: maritalStates().find(ms => ms.key === updatedCustomer.maritalState.name.charAt(0).toLowerCase() + updatedCustomer.maritalState.name.slice(1)),
          startDateCareer: updatedCustomer.startDateCareer,
          monthsIndependentBefore2021: updatedCustomer.monthsIndependentBefore2021,
          totalGrossRemuneration2020: updatedCustomer.totalGrossRemuneration2020,
        });
        commit('simulation/setDefaultValues', {
          maritalState: maritalStates().find(ms => ms.key === updatedCustomer.maritalState.name.charAt(0).toLowerCase() + updatedCustomer.maritalState.name.slice(1)),
          commissionBackService: state.primaryContract.commissionBackService,
          commissionPremium: state.primaryContract.commissionPremiumPensionAndDeath,
        },
        { root: true });
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setIsLoading', false);
  },
  // eslint-disable-next-line no-shadow
  async updateEnterprise({ commit, state, dispatch }, enterprise) {
    commit('setIsLoading', true);
    if (enterprise && state.customer) {
      const response = await enterpriseRepo.updateEnterprise(state.customer.customerId, enterprise);
      if (response && response.status === 200 && !response.errors) {
        commit('setEnterpriseDate', response.data.data.updateCustomerEnterprise.startDate);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setIsLoading', false);
  },
  async getSimulations({ commit, dispatch }, payload) {
    // commit('setIsLoading', true);
    if (payload.contractNumber && payload.language && payload.page) {
      const response = await simulationRepo
        .getSimulations(payload.contractNumber, payload.language, payload.page);
      if (response && response.status === 200 && !response.errors) {
        commit('setSimulations', response.data.data.simulations.items);
        commit('setOffset', response.data.data.simulations.lastItemIndex + 1);
        commit('setIsLastPage', response.data.data.simulations.isLastPage);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    // commit('setIsLoading', false);
  },
  // eslint-disable-next-line no-shadow
  async getSimulationPdf({ dispatch, commit, state }, data) {
    if (data.simulationId) {
      let language = '';
      switch (i18n.locale) {
        case ('nl'): language = 'nl-BE'; break;
        case ('en'): language = 'en-US'; break;
        case ('fr'): language = 'fr-FR'; break;
        default: language = 'nl-BE'; break;
      }

      commit('setSimulationLoading', { id: data.simulationId, loading: true });
      const response = await simulationRepo
        .getSimulationPdf(data.simulationId, language);
      if (response && response.status === 200 && !response.data.errors) {
        const file = new Blob([response.data], { type: 'application/pdf' });
        // var file = data['response'];

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(file, `simulation-${state.primaryContract.contractNumber}-${data.calculationDate}.pdf`);
          commit('setSimulationLoading', { id: data.simulationId, loading: false });
          return;
        }


        const a = document.createElement('a');
        a.style = 'display: none;';
        document.body.appendChild(a);

        const fileURL = URL.createObjectURL(file);

        a.href = fileURL;
        a.download = `simulation-${state.primaryContract.contractNumber}-${data.calculationDate}.pdf`; // gives it a name via an a tag
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          window.URL.revokeObjectURL(fileURL);
        }, 100);
      } else {
        dispatch('general/setError', true, { root: true });
      }
      commit('setSimulationLoading', { id: data.simulationId, loading: false });
    }
  },
  async setDefaultState({ commit }) {
    commit('setDefaultState');
  },
};


export default {
  state,
  mutations,
  getters,
  actions,
};
